import { computed, Injectable, InjectionToken, Provider, Signal, signal } from '@angular/core';

export interface LoadingProvider {
	isCurrentlyLoading(filter?: string, type?: 'requestKey' | 'uuid'): Signal<boolean>;
	setLoadingState(uuid: string, requestKey: string): string;
	clearLoadingState(uuid: string): void;
}

export const LOADING = new InjectionToken<LoadingProvider>('LOADING');

/**
 * Why does this service has "providedIn"?
 * It's because it's used by the desk component and sub components.
 * We want to make sure that only one instance exists (singleton) for the entire desk and its children. With this we don't have to reload all the data on navigation.
 */
@Injectable({
	providedIn: 'root',
})
export class LoadingService implements LoadingProvider {
	private readonly _loadingRequestsAndStates = signal<Record<string, string>>({});

	public isCurrentlyLoading(filter?: string, type?: 'requestKey' | 'uuid') {
		if (!filter) return computed(() => Object.keys(this._loadingRequestsAndStates()).length > 0);
		if (type === 'uuid') return computed(() => this._loadingRequestsAndStates()[filter] !== undefined);
		if (type === 'requestKey')
			return computed(() =>
				Object.keys(this._loadingRequestsAndStates()).some((key) => this._loadingRequestsAndStates()[key] === filter),
			);

		return computed(() => false);
	}

	public setLoadingState(uuid: string, requestKey: string) {
		this._loadingRequestsAndStates.update((current) => ({ ...current, [uuid]: requestKey }));
		return uuid;
	}

	public clearLoadingState(uuid: string) {
		this._loadingRequestsAndStates.update((current) => {
			const updated = { ...current };
			delete updated[uuid];
			return updated;
		});
	}
}

@Injectable({
	providedIn: 'root',
})
export class LoadingMock implements LoadingProvider {
	public isCurrentlyLoading(_filter?: string, _type?: 'requestKey' | 'uuid'): Signal<boolean> {
		return signal<boolean>(false);
	}
	public setLoadingState(_uuid: string, _requestKey: string): string {
		// mock implementation
		return _uuid;
	}
	public clearLoadingState(_uuid: string): void {
		// mock implementation
	}
}

export const provideLoading: Provider = {
	provide: LOADING,
	useClass: LoadingService,
};
